import { Check, X } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import type { ChangeProposal, ProposalStatus } from "@/types/agent";

interface ProposalCardProps {
  proposal: ChangeProposal & { status: ProposalStatus };
  onApprove: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
}

const STATUS_STYLES: Record<ProposalStatus, { bg: string; label: string }> = {
  pending: { bg: "border-amber-300 bg-amber-50", label: "" },
  approved: { bg: "border-blue-300 bg-blue-50", label: "Applying..." },
  applied: { bg: "border-green-300 bg-green-50", label: "Applied" },
  rejected: { bg: "border-muted bg-muted/30", label: "Rejected" },
  expired: { bg: "border-muted bg-muted/30", label: "Expired" },
};

const ACTION_LABELS: Record<string, string> = {
  update_fields: "Proposed changes",
  create_record: "Create new record",
  cancel_record: "Cancel record",
  delete_record: "Delete record",
  supersede_record: "Supersede record",
  add_area: "Add area",
  update_area: "Update area",
  remove_area: "Remove area",
  add_facility: "Add facility",
  remove_facility: "Remove facility",
  add_damage_provision: "Add damage provision",
  remove_damage_provision: "Remove damage provision",
  add_land_value: "Add land value",
  remove_land_value: "Remove land value",
};

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return "\u2014";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") return String(val);
  return String(val);
}

function isLifecycleAction(actionType?: string): boolean {
  return actionType === "cancel_record" || actionType === "delete_record" || actionType === "supersede_record";
}

export function ProposalCard({ proposal, onApprove, onReject }: ProposalCardProps): React.ReactElement {
  const style = STATUS_STYLES[proposal.status] ?? STATUS_STYLES.pending;
  const actionLabel = ACTION_LABELS[proposal.actionType ?? "update_fields"] ?? "Proposed changes";
  const lifecycle = isLifecycleAction(proposal.actionType);

  return (
    <div className={`my-2 rounded-lg border-2 p-3 ${style.bg}`} data-testid="proposal-card">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold">
          {actionLabel}
          {proposal.recordId ? ` \u2014 Record #${proposal.recordId}` : ""}
        </h4>
        {style.label && <span className="text-xs font-medium text-muted-foreground">{style.label}</span>}
      </div>

      {proposal.summary && lifecycle && <p className="mb-2 text-xs text-muted-foreground">{proposal.summary}</p>}

      {proposal.changes.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b">
              <th className="pb-1 text-left font-medium">Field</th>
              {!lifecycle && <th className="pb-1 text-left font-medium">Current</th>}
              <th className="pb-1 text-left font-medium">{lifecycle ? "Value" : "New"}</th>
            </tr>
          </thead>
          <tbody>
            {proposal.changes.map((change) => (
              <tr key={`${proposal.proposalId}-${change.field}`} className="border-b border-black/5">
                <td className="py-1 font-medium">{change.fieldLabel}</td>
                {!lifecycle && <td className="py-1 text-muted-foreground">{formatValue(change.from)}</td>}
                <td className="py-1 font-semibold">{formatValue(change.to)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {proposal.status === "pending" && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={() => onApprove(proposal.proposalId)} data-testid="proposal-approve">
            <Check className="mr-1 h-3 w-3" />
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReject(proposal.proposalId)}
            data-testid="proposal-reject"
          >
            <X className="mr-1 h-3 w-3" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );
}
